"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Rocket, ClipboardCheck, GraduationCap, TrendingUp, Users, ArrowRight, CircleCheck, ExternalLink } from "lucide-react";
const services = [{
  title: "Neural Ticket Routing",
  description: "Autonomous classification and distribution of complex IT issues using deep learning architectures.",
  icon: Rocket,
  colorClass: "text-cyan-400",
  bgIconClass: "bg-cyan-950/50",
  bgShapeClass: "bg-cyan-500/10",
  link: "/SupportHubPage"
}, {
  title: "Predictive Troubleshooting",
  description: "Identify and resolve infrastructure bottlenecks before they impact your operational workflow.",
  icon: ClipboardCheck,
  colorClass: "text-cyan-400",
  bgIconClass: "bg-cyan-950/50",
  bgShapeClass: "bg-cyan-500/10",
  link: "/SupportHubPage"
}, {
  title: "Autonomous Logic Engines",
  description: "Self-correcting scripts that handle repetitive maintenance tasks with zero human intervention.",
  icon: GraduationCap,
  colorClass: "text-cyan-400",
  bgIconClass: "bg-cyan-950/50",
  bgShapeClass: "bg-cyan-500/10",
  link: "/SupportHubPage"
}, {
  title: "Scalable Digital Agents",
  description: "Deploy an infinite workforce of AI support specialists that grow instantly with your demand.",
  icon: TrendingUp,
  colorClass: "text-cyan-400",
  bgIconClass: "bg-cyan-950/50",
  bgShapeClass: "bg-cyan-500/10",
  link: "/ServiceTiersPage"
}, {
  title: "Security Protocol AI",
  description: "Real-time threat detection and automated response protocols for enterprise-grade protection.",
  icon: Users,
  colorClass: "text-cyan-400",
  bgIconClass: "bg-cyan-950/50",
  bgShapeClass: "bg-cyan-500/10",
  link: "/SupportHubPage"
}];
export default function OurDigitalWorkforce() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: bannerRef,
    isVisible: isBannerVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="258793" id="our-digital-workforce" className="py-24 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#122b32_1px,transparent_1px),linear-gradient(to_bottom,#122b32_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none opacity-20" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-sm font-bold tracking-widest uppercase text-primary mb-4 block font-mono">
            Autonomous Capabilities
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 font-default tracking-tight">
            Our Digital Workforce
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            High-performance AI support agents engineered to solve complicated cases with maximum efficiency and minimal cost.
          </p>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, i) => <Card key={i} data-index={i} className={`relative overflow-hidden border-border/10 shadow-md hover:shadow-cyan-500/10 transition-all duration-300 hover:-translate-y-1 p-8 flex flex-col gap-0 group bg-card/40 backdrop-blur-md ${isGridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{
          transitionDelay: `${i * 100}ms`
        }}>
              {}
              <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-lg ${service.bgShapeClass} blur-2xl opacity-40 transition-transform duration-500 group-hover:scale-125`} />

              <div className={`w-12 h-12 rounded-lg border border-primary/20 ${service.bgIconClass} flex items-center justify-center mb-6 relative z-10 shadow-[0_0_15px_rgba(6,182,212,0.2)] group-hover:shadow-[0_0_25px_rgba(6,182,212,0.4)] transition-all`}>
                <service.icon className={`w-6 h-6 ${service.colorClass}`} />
              </div>

              <h3 className="text-xl font-bold text-foreground mb-3 font-default relative z-10 tracking-tight">
                {service.title}
              </h3>
              
              <p className="text-muted-foreground mb-8 flex-grow relative z-10 font-default leading-relaxed">
                {service.description}
              </p>

              <Link to={service.link} className={`inline-flex items-center font-mono text-xs uppercase tracking-wider ${service.colorClass} hover:opacity-100 opacity-70 transition-all relative z-10 w-fit`}>
                Learn Technical Specs <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-2" />
              </Link>
            </Card>)}
        </div>

        {}
        <div ref={bannerRef} className={`max-w-5xl mx-auto transition-all duration-700 delay-300 ${isBannerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Card className="bg-secondary/30 backdrop-blur-xl border-border/10 shadow-lg p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 md:gap-12 overflow-hidden relative">
            {}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-lg blur-3xl -z-10 opacity-30 translate-x-1/2 -translate-y-1/2" />
            
            <div className="w-full md:w-2/5 shrink-0">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686883/img/ai_architecture_diagram.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686883/img/ai_architecture_diagram.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686883/img/ai_architecture_diagram.png-wvc-sizes" alt="AI Tech Solutions Architecture" className="w-full h-auto rounded-lg shadow-md border border-primary/10" loading="lazy" />
            </div>
            
            <div className="w-full md:w-3/5 flex flex-col items-start">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary/10 text-primary text-xs font-bold tracking-widest uppercase mb-5 border border-primary/20">
                <CircleCheck className="w-4 h-4" /> Efficiency Verified
              </div>
              
              <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4 font-default tracking-tight">
                Enterprise Efficiency Standards
              </h3>
              
              <p className="text-muted-foreground mb-6 leading-relaxed font-default">
                AI Tech Solutions operates under strict computational efficiency protocols. Our digital agents are benchmarked against global IT support standards to ensure 99.9% resolution accuracy at a fraction of traditional costs.
              </p>
              
              <Link to="/ServiceTiersPage" className="inline-flex items-center font-mono text-sm text-primary hover:text-primary/80 transition-colors group">
                View Performance Metrics 
                <ExternalLink className="ml-2 w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </Card>
        </div>

      </div>
    </section>;
}